import type { PartnerCockpitSubscriptionCharge } from '@houzy/schemas/partner-cockpit/subscription';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatSwissAmount } from '../../../lib/formatSwissAmount';
import { formatSwissDate } from '../../../lib/time';
import { useChargeDocumentMutation } from '../api/subscriptionApi';
import {
    subscriptionChargeStatusLabel,
    subscriptionChargeTypeLabel,
    SUBSCRIPTION_CHARGE_STATUS_COLOR,
} from '../labels';

function DetailRow({ label, children }: { label: string; children: ReactNode }): ReactElement {
    return (
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
        </Stack>
    );
}

export type InvoiceDetailDialogProps = {
    charge: PartnerCockpitSubscriptionCharge | null;
    onClose: () => void;
};

export function InvoiceDetailDialog({ charge, onClose }: InvoiceDetailDialogProps): ReactElement | null {
    const { t } = useTranslation();
    const chargeDocument = useChargeDocumentMutation();

    if (!charge) {
        return null;
    }

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="invoice-detail-title">
            <DialogTitle id="invoice-detail-title" component="div" sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="overline">
                        {charge.invoiceNumber ?? t('SETTINGS.SUBSCRIPTION.BILLING.NO_NUMBER')}
                    </Typography>
                    <Chip
                        size="small"
                        color={SUBSCRIPTION_CHARGE_STATUS_COLOR[charge.status]}
                        label={subscriptionChargeStatusLabel(t, charge.status)}
                    />
                </Stack>
                <Typography variant="h5" component="h2">
                    {t('SETTINGS.SUBSCRIPTION.BILLING.AMOUNT_VALUE', {
                        amount: formatSwissAmount(charge.totalAmount),
                    })}
                </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={1.75}>
                    <DetailRow label={t('SETTINGS.SUBSCRIPTION.BILLING.INVOICE_NUMBER')}>
                        {charge.invoiceNumber ?? '—'}
                    </DetailRow>
                    <DetailRow label={t('SETTINGS.SUBSCRIPTION.BILLING.PERIOD')}>
                        {t('SETTINGS.SUBSCRIPTION.BILLING.PERIOD_RANGE', {
                            from: formatSwissDate(new Date(charge.periodStart)),
                            to: formatSwissDate(new Date(charge.periodEnd)),
                        })}
                    </DetailRow>
                    <DetailRow label={t('SETTINGS.SUBSCRIPTION.BILLING.DATE')}>
                        {charge.issuedAt ? formatSwissDate(new Date(charge.issuedAt)) : '—'}
                    </DetailRow>
                    <DetailRow label={t('SETTINGS.SUBSCRIPTION.BILLING.TYPE')}>
                        <Chip size="small" label={subscriptionChargeTypeLabel(t, charge)} />
                    </DetailRow>
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, py: 2 }}>
                {chargeDocument.isError && (
                    <Alert severity="error" sx={{ width: '100%' }}>
                        {t('ERRORS.GENERIC.DESCRIPTION')}
                    </Alert>
                )}
                {charge.hasInvoiceDocument ? (
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={chargeDocument.isPending}
                        onClick={(): void =>
                            chargeDocument.mutate(charge.uuid, {
                                onSuccess: ({ redirectUrl }) => {
                                    window.location.assign(redirectUrl);
                                },
                            })
                        }>
                        {t('SETTINGS.SUBSCRIPTION.BILLING.DOWNLOAD_INVOICE')}
                    </Button>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {t('SETTINGS.SUBSCRIPTION.BILLING.NO_DOCUMENT')}
                    </Typography>
                )}
                <Button fullWidth onClick={onClose}>
                    {t('ACTIONS.CLOSE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
